<!-- src/views/pages/dashboard/admin-dashboard/employee-status.vue -->
<script setup>
import { ref, computed, watch } from "vue";
import Vue3ChartJs from "@j-t-mcc/vue3-chartjs";
import { attendancejs as baseAttendanceConfig } from "./data";

import { useEmployeeStore } from "@/stores/employeeStore";
import { useAdminAttendanceStore } from "@/stores/adminAttendanceStore";
import defaultAvatar from "@/assets/img/users/user-01.jpg";

const props = defineProps({
  year: { type: Number, required: true },
});

// Stores
const employeeStore = useEmployeeStore();
const attendanceStore = useAdminAttendanceStore();

/* ---------------------------------------------
 * Period helpers (year-aware)
 * --------------------------------------------- */
function fmtYMD(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function startOfWeekMonday(d = new Date()) {
  const x = new Date(d);
  const day = x.getDay(); // 0 Sun, 1 Mon...
  const diff = (day === 0 ? -6 : 1) - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfWeekSunday(d = new Date()) {
  const s = startOfWeekMonday(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
}

function rangeForPeriod(period, year) {
  const now = new Date();
  const isCurrentYear = now.getFullYear() === year;

  // If not current year, anchor "this_week/this_month/today" to the same year
  const anchor = isCurrentYear ? now : new Date(year, 0, 1); // Jan 1 of selected year

  if (period === "today") {
    const d = new Date(anchor);
    d.setHours(0, 0, 0, 0);
    return { date_from: fmtYMD(d), date_to: fmtYMD(d) };
  }

  if (period === "this_week") {
    return {
      date_from: fmtYMD(startOfWeekMonday(anchor)),
      date_to: fmtYMD(endOfWeekSunday(anchor)),
    };
  }

  if (period === "this_month") {
    const y = anchor.getFullYear();
    const m = anchor.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0);
    return { date_from: fmtYMD(start), date_to: fmtYMD(end) };
  }

  // fallback: whole year
  return { date_from: `${year}-01-01`, date_to: `${year}-12-31` };
}

function getPeriodLabel(p) {
  if (p === "today") return "Today";
  if (p === "this_week") return "This Week";
  if (p === "this_month") return "This Month";
  return "This Week";
}

/* ---------------------------------------------
 * Filters
 * --------------------------------------------- */
const attendancePeriod = ref("today"); // for Attendance Overview donut
const clockingPeriod = ref("today"); // for Clock-In/Out card
const selectedDeptId = ref("all"); // "all" | number/string id
const employeeStatusFilter = ref("active"); // active | all

const departments = computed(() => {
  const emps = employeeStore.employees || [];
  const map = new Map();
  for (const e of emps) {
    const dep = e?.department;
    if (dep?.id && dep?.name) map.set(dep.id, dep.name);
  }
  return Array.from(map.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
});

const selectedDeptLabel = computed(() => {
  if (selectedDeptId.value === "all") return "All Departments";
  const d = departments.value.find(
    (x) => String(x.id) === String(selectedDeptId.value)
  );
  return d?.name || "All Departments";
});

const employeeStatusFilterLabel = computed(() =>
  employeeStatusFilter.value === "all" ? "All Employees" : "Active Employees"
);
const attendancePeriodLabel = computed(() =>
  getPeriodLabel(attendancePeriod.value)
);
const clockingPeriodLabel = computed(() =>
  getPeriodLabel(clockingPeriod.value)
);

/* ---------------------------------------------
 * Employees — workforce during selected year by employment type
 * --------------------------------------------- */
const employeesAll = computed(() => employeeStore.employees || []);
const currentYear = new Date().getFullYear();

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pickHireDate(e) {
  return (
    e.company_doj ||
    e.company_doj_formatted ||
    e.company_doj_label ||
    e.created_at ||
    null
  );
}

function pickEndDate(e) {
  return (
    e.employment_end_date ||
    e.date_left ||
    e.leaving_date ||
    e.terminated_at ||
    null
  );
}

function employedDuringYear(e, year) {
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999);
  const hire = parseDate(pickHireDate(e));
  if (!hire) return Number(year) === currentYear;
  if (hire > yearEnd) return false;
  const end = parseDate(pickEndDate(e));
  if (end && end < yearStart) return false;
  return true;
}

function normalizeEmploymentType(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function isActiveEmployee(e) {
  const status = String(e?.status || "").trim().toLowerCase();
  return !status || status === "active";
}

const employeesFiltered = computed(() => {
  const emps = employeesAll.value;
  if (!emps.length) return [];

  return emps.filter((e) => {
    if (!employedDuringYear(e, props.year)) return false;

    if (employeeStatusFilter.value === "active") {
      if (Number(props.year) === currentYear) {
        if (!isActiveEmployee(e)) return false;
      } else {
        // For past/future years: still employed at year-end
        const yearEnd = new Date(props.year, 11, 31, 23, 59, 59, 999);
        const end = parseDate(pickEndDate(e));
        if (end && end < yearEnd) return false;
      }
    }

    if (selectedDeptId.value !== "all") {
      const depId = e?.department?.id;
      if (String(depId) !== String(selectedDeptId.value)) return false;
    }
    return true;
  });
});

const totalEmployees = computed(() => employeesFiltered.value.length);

function countByType(type) {
  const target = normalizeEmploymentType(type);
  return employeesFiltered.value.filter(
    (e) => normalizeEmploymentType(e.employment_type) === target
  ).length;
}

const fulltimeCount = computed(() => countByType("Fulltime"));
const contractCount = computed(() => countByType("Contract"));
const probationCount = computed(() => countByType("Probation"));
const wfhCount = computed(() => countByType("WFH"));

const safePct = (count) => {
  const total = totalEmployees.value || 0;
  if (!total) return 0;
  return Math.round((count / total) * 100);
};

const fulltimePct = computed(() => safePct(fulltimeCount.value));
const contractPct = computed(() => safePct(contractCount.value));
const probationPct = computed(() => safePct(probationCount.value));
const wfhPct = computed(() => safePct(wfhCount.value));

/* ---------------------------------------------
 * Attendance Overview donut (backend filtered by period)
 * Labels/colors in data.js: Late, Present, Permission, Absent
 * --------------------------------------------- */
const attendanceStats = computed(() => attendanceStore.stats || {});

const presentCount = computed(() => attendanceStats.value.present || 0);
const lateCount = computed(() => attendanceStats.value.late || 0);
const permissionCount = computed(() => attendanceStats.value.on_leave || 0);
const absentCount = computed(() => attendanceStats.value.absent || 0);

const totalAttendanceCount = computed(
  () =>
    presentCount.value +
    lateCount.value +
    permissionCount.value +
    absentCount.value
);

const attendancePct = (part) => {
  const total = totalAttendanceCount.value;
  if (!total) return 0;
  return Math.round((part / total) * 100);
};

const presentPct = computed(() => attendancePct(presentCount.value));
const latePct = computed(() => attendancePct(lateCount.value));
const permissionPct = computed(() => attendancePct(permissionCount.value));
const absentPct = computed(() => attendancePct(absentCount.value));

const attendanceChartConfig = computed(() => {
  const cfg = JSON.parse(JSON.stringify(baseAttendanceConfig));
  // Must match labels order: Late, Present, Permission, Absent
  const data = [
    lateCount.value,
    presentCount.value,
    permissionCount.value,
    absentCount.value,
  ];
  if (cfg?.data?.datasets?.length) cfg.data.datasets[0].data = data;
  return cfg;
});

/* ---------------------------------------------
 * Recent clockings (backend filtered by dept + period)
 * --------------------------------------------- */
const recentClockings = computed(() => attendanceStore.recent || []);
const primary1 = computed(() => recentClockings.value[0] || null);
const primary2 = computed(() => recentClockings.value[1] || null);
const primary3 = computed(() => recentClockings.value[2] || null);

const lateHighlight = computed(
  () =>
    recentClockings.value.find(
      (r) => (r.status || "").toLowerCase() === "late"
    ) || null
);

/* ---------------------------------------------
 * Loaders (filter changes only — bootstrap handled by adminDashboardStore)
 * --------------------------------------------- */
async function loadAttendanceStats() {
  const r = rangeForPeriod(attendancePeriod.value, props.year);
  await attendanceStore.ensureStats({
    date_from: r.date_from,
    date_to: r.date_to,
  });
}

async function loadRecentClockings() {
  const r = rangeForPeriod(clockingPeriod.value, props.year);
  const params = {
    limit: 6,
    date_from: r.date_from,
    date_to: r.date_to,
  };

  // backend optional filter
  if (selectedDeptId.value !== "all") {
    params.department_id = selectedDeptId.value;
  }

  await attendanceStore.ensureRecent(params);
}

// attendance period dropdown changes
watch(attendancePeriod, loadAttendanceStats);

// clocking period or dept changes
watch([clockingPeriod, selectedDeptId], loadRecentClockings);

// year changes: refresh attendance/clocking for the selected year
watch(
  () => props.year,
  () => {
    loadAttendanceStats();
    loadRecentClockings();
  }
);
</script>

<template>
  <div class="row">
    <!-- Employee Status (period + department dynamic; uses NEW HIRES in range) -->
    <div class="col-xxl-4 d-flex">
      <div class="card flex-fill">
        <div
          class="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap"
        >
          <h5 class="mb-2">Employee Status ({{ year }})</h5>

          <div class="d-flex align-items-center gap-2">
            <!-- Workforce filter -->
            <div class="dropdown mb-2">
              <a
                href="javascript:void(0);"
                class="btn btn-white border btn-sm d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-users me-1"></i>{{ employeeStatusFilterLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="employeeStatusFilter = 'active'"
                    >Active Employees</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="employeeStatusFilter = 'all'"
                    >All Employees</a
                  >
                </li>
              </ul>
            </div>

            <!-- Department filter -->
            <div class="dropdown mb-2">
              <a
                href="javascript:void(0);"
                class="btn btn-white border btn-sm d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ selectedDeptLabel }}
              </a>
              <ul
                class="dropdown-menu dropdown-menu-end p-3"
                style="max-height: 280px; overflow: auto"
              >
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="selectedDeptId = 'all'"
                    >All Departments</a
                  >
                </li>
                <li v-if="departments.length">
                  <hr class="dropdown-divider my-2" />
                </li>
                <li v-for="d in departments" :key="`emp-status-${d.id}`">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="selectedDeptId = d.id"
                    >{{ d.name }}</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <p class="fs-13 mb-3">Total Employee</p>
            <h3 class="mb-3">{{ totalEmployees }}</h3>
          </div>

          <div
            v-if="!totalEmployees"
            class="border rounded p-3 text-muted small mb-3"
          >
            No employees match the selected filters.
          </div>

          <template v-else>
          <div class="progress-stacked emp-stack mb-3">
            <div
              class="progress"
              role="progressbar"
              aria-label="Fulltime"
              :aria-valuenow="fulltimePct"
              aria-valuemin="0"
              aria-valuemax="100"
              :style="{ width: fulltimePct + '%' }"
            >
              <div class="progress-bar bg-warning"></div>
            </div>
            <div
              class="progress"
              role="progressbar"
              aria-label="Contract"
              :aria-valuenow="contractPct"
              aria-valuemin="0"
              aria-valuemax="100"
              :style="{ width: contractPct + '%' }"
            >
              <div class="progress-bar bg-secondary"></div>
            </div>
            <div
              class="progress"
              role="progressbar"
              aria-label="Probation"
              :aria-valuenow="probationPct"
              aria-valuemin="0"
              aria-valuemax="100"
              :style="{ width: probationPct + '%' }"
            >
              <div class="progress-bar bg-danger"></div>
            </div>
            <div
              class="progress"
              role="progressbar"
              aria-label="WFH"
              :aria-valuenow="wfhPct"
              aria-valuemin="0"
              aria-valuemax="100"
              :style="{ width: wfhPct + '%' }"
            >
              <div class="progress-bar bg-pink"></div>
            </div>
          </div>

          <div class="border mb-3">
            <div class="row gx-0">
              <div class="col-6">
                <div class="p-2 flex-fill border-end border-bottom">
                  <p class="fs-13 mb-2">
                    <i class="ti ti-square-filled text-primary fs-12 me-2"></i
                    >Fulltime
                    <span class="text-gray-9">({{ fulltimePct }}%)</span>
                  </p>
                  <h2 class="display-1">{{ fulltimeCount }}</h2>
                </div>
              </div>
              <div class="col-6">
                <div class="p-2 flex-fill border-bottom text-end">
                  <p class="fs-13 mb-2">
                    <i class="ti ti-square-filled me-2 text-secondary fs-12"></i
                    >Contract
                    <span class="text-gray-9">({{ contractPct }}%)</span>
                  </p>
                  <h2 class="display-1">{{ contractCount }}</h2>
                </div>
              </div>
              <div class="col-6">
                <div class="p-2 flex-fill border-end">
                  <p class="fs-13 mb-2">
                    <i class="ti ti-square-filled me-2 text-danger fs-12"></i
                    >Probation
                    <span class="text-gray-9">({{ probationPct }}%)</span>
                  </p>
                  <h2 class="display-1">{{ probationCount }}</h2>
                </div>
              </div>
              <div class="col-6">
                <div class="p-2 flex-fill text-end">
                  <p class="fs-13 mb-2">
                    <i class="ti ti-square-filled text-pink me-2 fs-12"></i>WFH
                    <span class="text-gray-9">({{ wfhPct }}%)</span>
                  </p>
                  <h2 class="display-1">{{ wfhCount }}</h2>
                </div>
              </div>
            </div>
          </div>
          </template>

          <!-- Top performer stays static for now -->
          <!-- <h6 class="mb-2">Top Performer</h6>
          <div
            class="p-2 d-flex align-items-center justify-content-between border border-primary bg-primary-100 br-5 mb-4"
          >
            <div class="d-flex align-items-center overflow-hidden">
              <span class="me-2">
                <i class="ti ti-award-filled text-primary fs-24"></i>
              </span>
              <router-link
                to="/employee/employee-details"
                class="avatar avatar-md me-2"
              >
                <img
                  src="@/assets/img/profiles/avatar-24.jpg"
                  class="rounded-circle border border-white"
                  alt="img"
                />
              </router-link>
              <div>
                <h6 class="text-truncate mb-1 fs-14 fw-medium">
                  <router-link to="/employee/employee-details"
                    >Daniel Esbella</router-link
                  >
                </h6>
                <p class="fs-13">IOS Developer</p>
              </div>
            </div>
            <div class="text-end">
              <p class="fs-13 mb-1">Performance</p>
              <h5 class="text-primary">99%</h5>
            </div>
          </div> -->

          <router-link
            to="/employee/employee-list"
            class="btn btn-light btn-md w-100 mt-5"
          >
            View All Employees
          </router-link>
        </div>
      </div>
    </div>
    <!-- /Employee Status -->

    <!-- Attendance Overview (period dynamic; backend-filtered) -->
    <div class="col-xxl-4 col-xl-6 d-flex">
      <div class="card flex-fill">
        <div
          class="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap"
        >
          <h5 class="mb-2">Attendance Overview ({{ year }})</h5>
          <div class="dropdown mb-2">
            <a
              href="javascript:void(0);"
              class="btn btn-white border btn-sm d-inline-flex align-items-center"
              data-bs-toggle="dropdown"
            >
              <i class="ti ti-calendar me-1"></i>{{ attendancePeriodLabel }}
            </a>
            <ul class="dropdown-menu dropdown-menu-end p-3">
              <li>
                <a
                  href="javascript:void(0);"
                  class="dropdown-item rounded-1"
                  @click="attendancePeriod = 'this_month'"
                  >This Month</a
                >
              </li>
              <li>
                <a
                  href="javascript:void(0);"
                  class="dropdown-item rounded-1"
                  @click="attendancePeriod = 'this_week'"
                  >This Week</a
                >
              </li>
              <li>
                <a
                  href="javascript:void(0);"
                  class="dropdown-item rounded-1"
                  @click="attendancePeriod = 'today'"
                  >Today</a
                >
              </li>
            </ul>
          </div>
        </div>

        <div class="card-body">
          <div class="chartjs-wrapper-demo position-relative mb-4 dq-attendance-chart-wrap">
            <vue3-chart-js
              id="attendance"
              height="200"
              class="chartjs-chart"
              v-bind="attendanceChartConfig"
            ></vue3-chart-js>
            <div class="position-absolute text-center attendance-canvas dq-attendance-chart-center">
              <p class="fs-13 mb-1">Total Attendance</p>
              <h3>{{ totalAttendanceCount }}</h3>
            </div>
          </div>

          <h6 class="mb-3">Status</h6>
          <div class="d-flex align-items-center justify-content-between">
            <p class="f-13 mb-2">
              <i class="ti ti-circle-filled text-success me-1"></i>Present
            </p>
            <p class="f-13 fw-medium text-gray-9 mb-2">{{ presentPct }}%</p>
          </div>
          <div class="d-flex align-items-center justify-content-between">
            <p class="f-13 mb-2">
              <i class="ti ti-circle-filled text-secondary me-1"></i>Late
            </p>
            <p class="f-13 fw-medium text-gray-9 mb-2">{{ latePct }}%</p>
          </div>
          <div class="d-flex align-items-center justify-content-between">
            <p class="f-13 mb-2">
              <i class="ti ti-circle-filled text-warning me-1"></i>Permission
            </p>
            <p class="f-13 fw-medium text-gray-9 mb-2">{{ permissionPct }}%</p>
          </div>
          <div class="d-flex align-items-center justify-content-between mb-2">
            <p class="f-13 mb-2">
              <i class="ti ti-circle-filled text-danger me-1"></i>Absent
            </p>
            <p class="f-13 fw-medium text-gray-9 mb-2">{{ absentPct }}%</p>
          </div>

          <!-- <div
            class="bg-light br-5 box-shadow-xs p-2 pb-0 d-flex align-items-center justify-content-between flex-wrap"
          >
            <div class="d-flex align-items-center">
              <p class="mb-2 me-2">Total Absenties</p>
              <div class="avatar-list-stacked avatar-group-sm mb-2">
                <span class="avatar avatar-rounded">
                  <img
                    class="border border-white"
                    src="@/assets/img/profiles/avatar-27.jpg"
                    alt="img"
                  />
                </span>
                <span class="avatar avatar-rounded">
                  <img
                    class="border border-white"
                    src="@/assets/img/profiles/avatar-30.jpg"
                    alt="img"
                  />
                </span>
                <span class="avatar avatar-rounded">
                  <img src="@/assets/img/profiles/avatar-14.jpg" alt="img" />
                </span>
                <span class="avatar avatar-rounded">
                  <img src="@/assets/img/profiles/avatar-29.jpg" alt="img" />
                </span>
                <a
                  class="avatar bg-primary avatar-rounded text-fixed-white fs-10"
                  href="javascript:void(0);"
                >
                  +1
                </a>
              </div>
            </div>
            <router-link
              to="/attendance/attendance-admin"
              class="fs-13 link-primary text-decoration-underline mb-2"
              >View Details</router-link
            >
          </div> -->
        </div>
      </div>
    </div>
    <!-- /Attendance Overview -->

    <!-- Clock-In/Out (department + period dynamic; backend-filtered) -->
    <div class="col-xxl-4 col-xl-6 d-flex">
      <div class="card flex-fill">
        <div
          class="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap"
        >
          <h5 class="mb-2">Clock-In/Out ({{ year }})</h5>
          <div class="d-flex align-items-center">
            <!-- Dept filter (dynamic) -->
            <div class="dropdown mb-2">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white btn-sm d-inline-flex align-items-center border-0 fs-13 me-2"
                data-bs-toggle="dropdown"
              >
                {{ selectedDeptLabel }}
              </a>
              <ul
                class="dropdown-menu dropdown-menu-end p-3"
                style="max-height: 280px; overflow: auto"
              >
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="selectedDeptId = 'all'"
                    >All Departments</a
                  >
                </li>
                <li v-if="departments.length">
                  <hr class="dropdown-divider my-2" />
                </li>
                <li v-for="d in departments" :key="d.id">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="selectedDeptId = d.id"
                    >{{ d.name }}</a
                  >
                </li>
                <li
                  v-if="!departments.length"
                  class="px-2 py-1 text-muted small"
                >
                  No departments yet
                </li>
              </ul>
            </div>

            <!-- Period filter -->
            <div class="dropdown mb-2">
              <a
                href="javascript:void(0);"
                class="btn btn-white border btn-sm d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-calendar me-1"></i>{{ clockingPeriodLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="clockingPeriod = 'this_month'"
                    >This Month</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="clockingPeriod = 'this_week'"
                    >This Week</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="clockingPeriod = 'today'"
                    >Today</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body">
          <div
            v-if="!recentClockings.length"
            class="border rounded p-3 text-muted small mb-3"
          >
            No clock-in records for the selected filters.
          </div>

          <div v-else>
            <!-- 1st record -->
            <div
              class="d-flex align-items-center justify-content-between mb-3 p-2 border border-dashed br-5 dq-clock-row"
            >
              <div class="d-flex align-items-center">
                <a href="javascript:void(0);" class="avatar flex-shrink-0">
                  <img
                    :src="primary1?.avatar_url || defaultAvatar"
                    class="rounded-circle border border-2"
                    alt="img"
                  />
                </a>
                <div class="ms-2">
                  <h6 class="fs-14 fw-medium text-truncate">
                    {{ primary1?.employee_name || "-" }}
                  </h6>
                  <p class="fs-13">
                    {{ primary1?.designation_name || "-" }}
                  </p>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <a href="javascript:void(0);" class="link-default me-2"
                  ><i class="ti ti-clock-share"></i
                ></a>
                <span
                  class="fs-10 fw-medium d-inline-flex align-items-center badge badge-success"
                >
                  <i class="ti ti-circle-filled fs-5 me-1"></i>
                  {{ primary1?.check_in_label || "-" }}
                </span>
              </div>
            </div>

            <!-- 2nd record -->
            <div
              class="d-flex align-items-center justify-content-between mb-3 p-2 border br-5 dq-clock-row"
            >
              <div class="d-flex align-items-center">
                <a href="javascript:void(0);" class="avatar flex-shrink-0">
                  <img
                    :src="primary2?.avatar_url || defaultAvatar"
                    class="rounded-circle border border-2"
                    alt="img"
                  />
                </a>
                <div class="ms-2">
                  <h6 class="fs-14 fw-medium">
                    {{ primary2?.employee_name || "-" }}
                  </h6>
                  <p class="fs-13">
                    {{ primary2?.designation_name || "-" }}
                  </p>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <a href="javascript:void(0);" class="link-default me-2"
                  ><i class="ti ti-clock-share"></i
                ></a>
                <span
                  class="fs-10 fw-medium d-inline-flex align-items-center badge badge-success"
                >
                  <i class="ti ti-circle-filled fs-5 me-1"></i>
                  {{ primary2?.check_in_label || "-" }}
                </span>
              </div>
            </div>

            <!-- 3rd record (detailed) -->
            <div class="mb-3 p-2 border br-5 dq-clock-row-detail">
              <div class="d-flex align-items-center justify-content-between dq-clock-row">
                <div class="d-flex align-items-center">
                  <a href="javascript:void(0);" class="avatar flex-shrink-0">
                    <img
                      :src="primary3?.avatar_url || defaultAvatar"
                      class="rounded-circle border border-2"
                      alt="img"
                    />
                  </a>
                  <div class="ms-2">
                    <h6 class="fs-14 fw-medium text-truncate">
                      {{ primary3?.employee_name || "-" }}
                    </h6>
                    <p class="fs-13">
                      {{ primary3?.designation_name || "-" }}
                    </p>
                  </div>
                </div>
                <div class="d-flex align-items-center">
                  <a href="javascript:void(0);" class="link-default me-2"
                    ><i class="ti ti-clock-share"></i
                  ></a>
                  <span
                    class="fs-10 fw-medium d-inline-flex align-items-center badge badge-success"
                  >
                    <i class="ti ti-circle-filled fs-5 me-1"></i>
                    {{ primary3?.check_in_label || "-" }}
                  </span>
                </div>
              </div>

              <div
                class="d-flex align-items-center justify-content-between flex-wrap mt-2 border br-5 p-2 pb-0 dq-clock-stats"
              >
                <div>
                  <p class="mb-1 d-inline-flex align-items-center">
                    <i class="ti ti-circle-filled text-success fs-5 me-1"></i
                    >Clock In
                  </p>
                  <h6 class="fs-13 fw-normal mb-2">
                    {{ primary3?.check_in_label || "-" }}
                  </h6>
                </div>
                <div>
                  <p class="mb-1 d-inline-flex align-items-center">
                    <i class="ti ti-circle-filled text-danger fs-5 me-1"></i
                    >Clock Out
                  </p>
                  <h6 class="fs-13 fw-normal mb-2">
                    {{ primary3?.check_out_label || "-" }}
                  </h6>
                </div>
                <div>
                  <p class="mb-1 d-inline-flex align-items-center">
                    <i class="ti ti-circle-filled text-warning fs-5 me-1"></i
                    >Production
                  </p>
                  <h6 class="fs-13 fw-normal mb-2">
                    {{ primary3?.production_label || "-" }}
                  </h6>
                </div>
              </div>
            </div>
          </div>

          <!-- Late highlight -->
          <template v-if="recentClockings.length">
          <h6 class="mb-2">Late</h6>
          <div
            class="d-flex align-items-center justify-content-between mb-3 p-2 border border-dashed br-5"
          >
            <div class="d-flex align-items-center">
              <span class="avatar flex-shrink-0">
                <img
                  :src="lateHighlight?.avatar_url || defaultAvatar"
                  class="rounded-circle border border-2"
                  alt="img"
                />
              </span>
              <div class="ms-2">
                <h6 class="fs-14 fw-medium text-truncate">
                  {{ lateHighlight?.employee_name || "-" }}
                  <span
                    class="fs-10 fw-medium d-inline-flex align-items-center badge badge-success"
                  >
                    <i class="ti ti-clock-hour-11 me-1"></i>
                    {{ lateHighlight?.late_label || "-" }}
                  </span>
                </h6>
                <p class="fs-13">
                  {{ lateHighlight?.designation_name || "-" }}
                </p>
              </div>
            </div>
            <div class="d-flex align-items-center">
              <a href="javascript:void(0);" class="link-default me-2"
                ><i class="ti ti-clock-share"></i
              ></a>
              <span
                class="fs-10 fw-medium d-inline-flex align-items-center badge badge-danger"
              >
                <i class="ti ti-circle-filled fs-5 me-1"></i>
                {{ lateHighlight?.check_in_label || "-" }}
              </span>
            </div>
          </div>
          </template>

          <router-link
            to="/reports/attendance-report"
            class="btn btn-light btn-md w-100"
          >
            View All Attendance
          </router-link>
        </div>
      </div>
    </div>
    <!-- /Clock-In/Out -->
  </div>
</template>
